import { defineStore } from 'pinia'
import type {
  ApiErrorInterface,
  AppSettingsInterface,
  CountryInterface,
  MediaInterface,
  PermissionsMatrixInterface,
  RegionInterface,
  ValidatorResultInterface
} from '@/types/app'
import type {
  BundleInterface,
  FeeInterface,
  ItineraryInterface,
  ItineraryService,
  ServiceInterface,
  ServicePriceInterface,
  TagInterface,
  TripInterface,
  VersionConfigurationInterface,
  VersionInterface
} from '@/types/trips'
import type { AdminUserInterface, AuthenticatedUserInterface, RoleInterface, UserInterface } from '@/types/user'
import type { SupplierInterface } from '@/types/suppliers'
import type { CommentInterface, ContactInterface } from '@/types/contacts'
import type { FilterDescriptor } from '@progress/kendo-data-query'
import { useTripBuilderStore } from '@/stores/trip-builder'
import { api } from '@/api/client'
import { toRaw } from 'vue'
import { IntlService } from '@progress/kendo-vue-intl'
import { labels } from '@/appdata/labels'
import { dateSorter, handleError } from '@/appdata/globals'
import { isSameRecord } from '@/appdata/identity'
import { useServiceManagerStore } from '@/stores/service-manager'
import { useAuthStore } from '@/stores/auth'
import { handleErrorResponse} from '@/appdata/globals'
import * as bootstrap from 'bootstrap'

const kendo: IntlService = new IntlService('en-US')
export const roundNumbers = (value: number)  => {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

let _loadingCount = 0
let _storeTripInflight: Promise<TripInterface | null> | null = null

/** @internal - exported only for unit tests */
export const VERSIONED_TRIP_FIELDS = [
  'dateDeparture',
  'duration',
  'numberOfPassengers',
  'proposalTotal',
  'primaryContact',
  'contacts',
] as const satisfies ReadonlyArray<keyof TripInterface>

export function buildConfigurationFromTrip(trip: TripInterface): VersionConfigurationInterface {
  return {
    dateDeparture: trip.dateDeparture,
    duration: trip.duration,
    numberOfPassengers: trip.numberOfPassengers,
    proposalTotal: trip.proposalTotal,
    primaryContact: trip.primaryContact?.uuid
      ? { uuid: trip.primaryContact.uuid }
      : null,
    contacts: (trip.contacts ?? [])
      .filter(contact => contact.uuid)
      .map(contact => ({ uuid: contact.uuid! })),
  }
}

export const useAppDataStore = defineStore('appData',{
  state: () => {
    _loadingCount = 0
    _storeTripInflight = null
    return {
    loading: true as boolean,
    printStatusUrl: null as string|null,
    currentUser: null as AuthenticatedUserInterface | null,
    roles: [] as RoleInterface[],
    types: [] as TagInterface[],
    countries: [] as CountryInterface[],
    country: null as CountryInterface | null,
    regions: [] as RegionInterface[],
    region: null as RegionInterface | null,
    salesTeam: [] as UserInterface[],
    opsTeam: [] as UserInterface[],
    settings: {
      exchangeRate: 1 as number,
      agentCommission: 15 as number|null,
      creditCardSurcharge: 3.5 as number,
      markup: 10 as number,
      termsConditions: "" as string,
      termsConditionsServices: "" as string,
      termsConditionsTransportation: "" as string,
      defaultCoverImage: null,
      defaultHeaderImage: null
    } as AppSettingsInterface,
    notifications: {
      success: [] as string[],
      errors: [] as string[],
      warnings: [] as string[],
      info: [] as string[],
    },
    permissions: {
      users: { read: false, write: false, delete: false },
      settings: { read: false, write: false, delete: false },
      media: { read: false, write: false, delete: false },
      contacts: { read: false, write: false, delete: false },
      services: { read: false, write: false, delete: false },
      packages: { read: false, write: false, delete: false },
      suppliers: { read: false, write: false, delete: false },
      trips: { read: false, write: false, delete: false },
    } as PermissionsMatrixInterface,

    filters: {
      searchQuery: "" as string,
    },
    links: {
      next: null as string | null,
      previous: null as string | null,
    },

    records: {
      total: null as number | null,
      start: null as number | null,
      end: null as number | null,
    },

    trips: [] as TripInterface[],
    trip: null as TripInterface | null,

    suppliers: [] as SupplierInterface[],
    supplier: null as SupplierInterface | null,

    services: [] as ServiceInterface[],
    service: null as ServiceInterface | null,

    contacts: [] as ContactInterface[],
    contact: null as ContactInterface | null,

    bundles: [] as BundleInterface[],
    bundle: null as BundleInterface | null,

    tags: [] as TagInterface[],
    tag: null as TagInterface | null,

    media: [] as MediaInterface[],
    asset: null as MediaInterface | null,

    users: [] as AdminUserInterface[],
    user: null as AdminUserInterface | null,
  }},
  getters: {
    isLockedByOther(state): boolean {
      return !!(state.trip?.lock && !state.trip?.editable)
    },
    proposalServiceTotal(state): number {
      let total = 0;

      if (state.trip && state.trip?.itinerary) {
        state.trip?.itinerary.filter(item => {
          return item.version?.uuid === state.trip?.currentVersion?.uuid;
        }).forEach(item => {

          total = total + useAppDataStore().lineItemTotal(item);

        });
      }

      return total;
    },
    proposalTotal(_state): number {
      console.info("AppDataStore | proposalTotal")

      let total = this.proposalCommissionBaseTotal;
      console.debug("AppDataStore | proposalTotal | ", total);

      const commission = useAppDataStore().commissionAmount(total);
      console.debug("AppDataStore | proposalTotal | ", commission);
      total = total + commission;

      console.debug("AppDataStore | proposalTotal | ", total);
      return roundNumbers(total);
    },
    proposalCommissionBaseTotal(state): number {
      const serviceTotal = this.proposalServiceTotal;
      let total = serviceTotal;

      // console.debug("Service Total", serviceTotal);
      // console.debug("Fees", state.trip?.fees)
      if (state.trip && state.trip.fees) {
        console.debug("Fees", state.trip.fees.length);
        state.trip.fees.forEach((fee: FeeInterface) => {
          console.log(fee.amount, fee.rate);
          if (!fee.amount) {
            fee.amount = null;
          }

          const feeAmount = fee.amount ?? ((fee.rate ?? 0) * serviceTotal);
          // console.debug("Fees", feeAmount, fee);
          total = total + roundNumbers(feeAmount);
        });
      }

      return roundNumbers(total);
    },
    proposalDepartureDate(state): Date {
      if (state.trip?.dateDeparture !== null) {
        return useAppDataStore().agnosticDate(state.trip?.dateDeparture ?? "") ?? new Date();
      }

      return new Date();
    },
    proposalReturnDate(state): Date {
      if (state.trip?.dateDeparture !== null) {
        const departure = useAppDataStore().agnosticDate(state.trip?.dateDeparture) ?? new Date();

        return departure.addDays((state.trip?.duration ?? 1) - 1);
      }

      return new Date();
    },
    agentLabel(state): string | null {
      if (state.trip?.agent) {
        if ((!state.trip?.agent.name) && state.trip?.agent.companyName) {
          return state.trip.agent.companyName;
        }

        let returnValue = state.trip?.agent.name;
        if (state.trip?.agent.companyName) {
          returnValue = returnValue + ' ('+ state.trip?.agent.companyName +')';
        }

        return returnValue ?? null;
      }

      return null;
    },
    hotels(state): ItineraryService[] {
      if (!state.trip) {
        return [];
      }

      const hotels: ItineraryService[] = [];
      state.trip.itinerary
        ?.filter((item: ItineraryInterface) => {
          return (item.version?.uuid === state.trip?.currentVersion?.uuid);
        })
        .sort(dateSorter)
        .forEach((day: ItineraryInterface) => {
          const dailyHotels = day.services
            ?.filter((item: ItineraryService) => {
              return (item.service?.type?.label === 'hotel' || !item.service)
            })

          if (Array.isArray(dailyHotels)) {
            hotels.push(...dailyHotels);
          }
        })

      return hotels;
    },
    activities(state): ItineraryService[] {
      if (!state.trip) {
        return [];
      }

      const activities: ItineraryService[] = [];
      state.trip.itinerary
        ?.filter((item: ItineraryInterface) => {
          return (item.version?.uuid === state.trip?.currentVersion?.uuid);
        })
        .sort(dateSorter)
        .forEach((day: ItineraryInterface) => {
          const dailyActivities = day.services
            ?.filter((item: ItineraryService) => {
              return (item.service?.type?.label !== 'hotel' || !item.service)
            })

          if (Array.isArray(dailyActivities)) {
            activities.push(...dailyActivities);
          }
        })

      return activities;
    },
    cities(state): ItineraryInterface[] {
      if (!state.trip) {
        return [];
      }

      return state.trip?.itinerary?.filter((item) => {
        return ((!state.trip?.currentVersion) || item.version?.uuid === state.trip?.currentVersion?.uuid);
      }).sort(dateSorter) ?? [];
    },
    contactBirthDate(state): Date {
      if (state.contact?.dateBirth) {
        return useAppDataStore().agnosticDate(state.contact?.dateBirth ?? "") ?? new Date();
      }

      return new Date();
    },
    servicesBySupplier(state): SupplierInterface[] {
      const serviceManager = useServiceManagerStore();
      console.debug("AppDataStore | servicesBySupplier | ", state.suppliers)

      const result = state.suppliers.map((supplier: SupplierInterface) => {
        console.debug(supplier.name, supplier.services, supplier.services?.length);
        return {
          ...supplier,
          services: supplier.services?.filter((service: ServiceInterface) => {
            console.debug("AppDataStore | servicesBySupplier | ", serviceManager.matchedService(supplier, service))
            return serviceManager.matchedService(supplier, service);
          }).map((service: ServiceInterface) => {
            return {
              ...service,
              supplier: supplier
            }
          }) ?? []
        }
      })

      console.debug("AppDataStore | servicesBySupplier | ", result)

      return result;
    },

    // ------------------------------------------
    // + Itinerary
    // ------------------------------------------
    currentItinerary(state): ItineraryInterface[] {
      if (!Array.isArray(state.trip?.itinerary)) return [];

      return state.trip.itinerary.filter((element: ItineraryInterface) => {
          return element.version?.uuid === state.trip?.currentVersion?.uuid;
        })
        .sort((element1: ItineraryInterface, element2: ItineraryInterface) => {
          if ((element1.dateScheduled ?? "") < (element2.dateScheduled ?? "")) {
            return -1;
          }
          else if ((element1.dateScheduled ?? "") > (element2.dateScheduled ?? "")) {
            return 1;
          }

          return 0
        })
    },
    finalizedItinerary(state): ItineraryInterface[] {
      if (!Array.isArray(state.trip?.itinerary)) return [];

      const finalVersion = state.trip?.versions?.filter((item: VersionInterface) => {
        return item.final
      })?.at(0) ?? null;

      return state.trip.itinerary.filter((element: ItineraryInterface) => {
          return element.version?.uuid === (finalVersion?.uuid ?? state.trip?.currentVersion?.uuid);
        })
        .sort((element1: ItineraryInterface, element2: ItineraryInterface) => {
          if ((element1.dateScheduled ?? "") < (element2.dateScheduled ?? "")) {
            return -1;
          }
          else if ((element1.dateScheduled ?? "") > (element2.dateScheduled ?? "")) {
            return 1;
          }

          return 0
        })
    },

    // ------------------------------------------
    // + Date Standardization
    // ------------------------------------------
    clientTimezoneOffset(_state): number {
      return new Date().getTimezoneOffset() * -1;
    },
    clientTimezoneInterval(_state): string {
      const totalMinutes = this.clientTimezoneOffset;
      console.debug("AppData | clientTimezoneInterval | ", totalMinutes)
      if (totalMinutes !== 0) {
        const hours = Math.floor(Math.abs(totalMinutes) / 60);
        const minutes = Math.abs(totalMinutes) % 60;

        const sign = totalMinutes > 0 ? '+' : '-';

        return `${sign}${hours.toString().padStart(2,'0')}:${minutes.toString().padStart(2, '0')}`
      }

      return 'Z';
    }
  },
  actions: {
    async initializeAppData(cancelLoadIndicator: boolean = false) {
      console.info("AppDataStore | initializeAppData");

      if (useAuthStore().authenticated) {
        if (!this.loading) {
          this.setLoading(true)
        }

        await this.fetchSalesTeam();
        await this.fetchOpsTeam();
        await this.fetchPermissions();
        await this.fetchCountries();
        await this.fetchRegions();
        await this.fetchTypes();
        return await this.fetchAppSettings().then(() => {
          if (cancelLoadIndicator) {
            this.setLoading(false)
          }
        });
      }
    },
    bindTooltips(selector: string): void {
      console.info("AppData | bindTooltips")
      const tooltipTriggerList = document.querySelectorAll(selector + ' [data-bs-toggle="tooltip"]');
      [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl))
    },

    filtersToQueryString(filters: Array<FilterDescriptor>) {
      return filters.map((item: FilterDescriptor, index: number) => {
        return [
          "filter[filters]["+index+"][field]="+item.field,
          "filter[filters]["+index+"][operator]="+item.operator.toString(),
          ...Array.isArray(item.value)
            ? item.value.map((value, index2) => {
                return "filter[filters]["+index+"][value]["+index2+"]="+encodeURIComponent(value)
              })
            : ["filter[filters]["+index+"][value]="+encodeURIComponent(item.value)],
        ].join('&')
      }).join('&') + "&filter[logic]=and";
    },
    serviceDataItem(proxy: ItineraryService|null): ItineraryService|null {
      if (!proxy) {
        return null;
      }

      // A row that has never been saved has no uuid to match on, and comparing
      // `undefined === undefined` below would resolve it to the first unsaved
      // row anywhere in the itinerary rather than the one being edited.
      if (!proxy.uuid) {
        return proxy;
      }

      let actual: ItineraryService|null = null;
      this.trip?.itinerary?.forEach((el: ItineraryInterface) => {
        if (actual !== null) return;

        actual = el.services?.find((service: ItineraryService) => {
          return service.uuid === proxy.uuid;
        }) ?? null;
      })

      return actual;
    },
    numberOfNights(trip: TripInterface|null, location: TagInterface|null) {
      console.info("AppDataStore | numberOfNights")
      console.debug("AppDataStore | numberOfNights | trip | ", trip)
      console.debug("AppDataStore | numberOfNights | location | ", location)
      if ((!trip) || (!location)) return 0;

      return (trip.itinerary?.filter((day: ItineraryInterface) => {
        return day.location?.uuid === location.uuid;
      }) ?? []).length ?? 0;
    },

    servicesSortedByStartTime(services: ItineraryService[]) {
      return services.sort((element1: ItineraryService, element2: ItineraryService) => {
        if ((element1.time ?? "") < (element2.time ?? "")) {
          return -1;
        }
        else if ((element1.time ?? "") > (element2.time ?? "")) {
          return 1;
        }

        return 0
      })
    },

    async fetchTrips(url: string | null, filters?: Array<FilterDescriptor>) {
      if (!url) {
        url = "/v1/trip"
      }

      if (filters && filters?.length > 0) {
        const compiled = this.filtersToQueryString(filters ?? []);
        url = url + '?' + compiled;
      }

      this.setLoading(true)
      return api.get(url)
        .then((response) => {
          this.$patch({
            trips: response.data.results,
            links: {
              next: response.data.links.next,
              previous: response.data.links.previous
            },
            records: {
              total: response.data.metadata.total,
              start: response.data.metadata.start,
              end: response.data.metadata.end,
            },
          })
        })
        .then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    _handleMutationError(error: ApiErrorInterface) {
      handleError(error)
      if (error.response?.status === 423 && this.trip) {
        return this.fetchTrip(this.trip).then(() => {
          if (this.trip && !this.trip.lock) {
            return this.acquireLock(this.trip)
          }
        }).then(() => Promise.reject(error))
      }
      return Promise.reject(error)
    },
    async fetchTrip(item: TripInterface|null) {
      if (!item) return;
      this.setLoading(true)

      const url = '/v1/trip/' + item.uuid;
      await api.get(url)
        .then((response) => {
          this.$patch({
            trip: response.data,
          })
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
        .finally(() => {
          this.setLoading(false)
       })
    },
    storeTrip(trip: TripInterface | null) {
      if (trip === null) return Promise.resolve(null);
      if (trip?.lock && !trip?.editable) return Promise.reject();
      if (!trip?.editable && !useAuthStore().isOpsTeam) return Promise.resolve(null);

      if (_storeTripInflight) return _storeTripInflight;

      const validatorResult = this.validateTrip(trip);
      if (!validatorResult?.valid) {
        this.warnUser({
          message: validatorResult.message,
        })

        const tripBuilder = useTripBuilderStore();
        if (validatorResult.targetTab && (validatorResult.targetTab !== tripBuilder.selectedTab)) {
          tripBuilder.$patch({ selectedTab: validatorResult.targetTab })
        }

        return Promise.resolve(null);
      }

      // Remove line items that have no service set and sync tripDay references
      if (trip.itinerary?.length) {
        for (const day of trip.itinerary) {
          if (Array.isArray(day.services)) {
            day.services = day.services.filter(s => s.service);

            const tripDay: ItineraryInterface = {
              uuid: day.uuid,
              trip: { uuid: trip.uuid },
              dateScheduled: day.dateScheduled,
              location: day.location,
              version: day.version,
            };
            day.services.forEach((service: ItineraryService) => {
              service.tripDay = tripDay;
            });
          }
        }
      }

      this.setLoading(true);

      const url = '/v1/trip';
      const isNewTrip = !trip.uuid;
      _storeTripInflight = api[isNewTrip ? 'post' : 'patch'](url, trip)
        .then(async (response) => {
          this.$patch({ trip: response.data.trip });

          if (isNewTrip && response.data.trip?.uuid) {
            await this.acquireLock(response.data.trip);
          }

          const refreshed = await this._syncCurrentVersionConfiguration(response.data.trip)

          return refreshed ?? response.data.trip;
        })
        .catch((error) => this._handleMutationError(error))
        .finally(() => {
          _storeTripInflight = null;
          this.setLoading(false);
        });

      return _storeTripInflight;
    },

    async patchTrip(fields: Partial<TripInterface>) {
      if (!this.trip?.uuid) return null;
      if (this.trip?.lock && !this.trip?.editable) return Promise.reject();
      if (!this.trip?.editable) return null;

      const payload = { uuid: this.trip.uuid, ...fields };

      return api.patch('/v1/trip', payload)
        .then(async (response) => {
          const updatedTrip = response.data.trip
          if (VERSIONED_TRIP_FIELDS.some(key => key in fields)) {
            const refreshed = await this._syncCurrentVersionConfiguration(updatedTrip)
            return refreshed ?? updatedTrip
          }
          return updatedTrip
        })
        .catch((error) => this._handleMutationError(error))
    },

    async acquireLock(trip: TripInterface) {
      if (!trip?.uuid) return

      await api.put(`/v1/trip/${trip.uuid}/lock`, {})
        .then((response) => {
          if (this.trip && this.trip.uuid === trip.uuid) {
            this.$patch({ trip: { lock: response.data.lock } })
          }
        })
        .catch((error) => {
          if (error.response?.status !== 423) {
            handleError(error)
          }
        })
    },

    async releaseLock(trip: TripInterface) {
      if (!trip?.uuid) return

      await api.delete(`/v1/trip/${trip.uuid}/lock`)
        .then(() => {
          if (this.trip && this.trip.uuid === trip.uuid) {
            this.$patch({ trip: { lock: null } })
          }
        })
        .catch((error) => {
          if (error.response?.status !== 423) {
            handleError(error)
          }
        })
    },

    async adminReleaseLock(trip: TripInterface) {
      if (!trip?.uuid) return

      await api.delete(`/v1/trip/${trip.uuid}/lock`)
        .catch((error) => {
          if (error.response?.status !== 423) {
            handleError(error)
          }
        })

      // Re-fetch trip to update editable and lock state
      if (this.trip && this.trip.uuid === trip.uuid) {
        await this.fetchTrip(this.trip)
        const refreshedTrip = this.trip
        if (refreshedTrip) await this.acquireLock(refreshedTrip)
      }
    },

    async storeVersion(version?: VersionInterface|null) {
      if (!version) return;

      const url = '/v1/version/'+ version.uuid;

      return api.patch(url, version)
        .then((response) => {
          return response.data.version;
        })
        .finally(() => {
          this.setLoading(false)
        })
    },

    async patchVersionConfiguration(
      version: VersionInterface | null | undefined,
      configuration: VersionConfigurationInterface,
    ) {
      if (!version?.uuid) return
      return api.patch(`/v1/version/${version.uuid}`, { configuration })
        .then((response) => response.data.version)
        .catch((error) => { handleError(error) })
    },

    async _syncCurrentVersionConfiguration(trip: TripInterface | null | undefined) {
      if (!trip?.currentVersion?.uuid) return
      const snapshot = buildConfigurationFromTrip(trip)
      // Await the config write, then refetch so trip.versions[] reflects the committed
      // configuration. The trip-write response predates this version-config write, so
      // without the refetch a later trip refresh would echo the pre-write config and
      // selecting a version would rehydrate (and re-persist) stale values — losing edits
      // across version switches.
      await this.patchVersionConfiguration(trip.currentVersion, snapshot)
      await this.fetchTrip(trip)
      return this.trip
    },

    async applyVersionConfiguration(
      trip: TripInterface,
      configuration: VersionConfigurationInterface,
    ): Promise<void> {
      trip.dateDeparture = configuration.dateDeparture
      trip.duration = configuration.duration
      trip.numberOfPassengers = configuration.numberOfPassengers
      trip.proposalTotal = configuration.proposalTotal

      const resolvedContacts: ContactInterface[] = []
      for (const contactRef of configuration.contacts) {
        const existingContact = trip.contacts?.find(contact => contact.uuid === contactRef.uuid)
        if (existingContact) {
          resolvedContacts.push(existingContact)
          continue
        }
        try {
          const response = await api.get('/v1/contact/' + contactRef.uuid)
          if (response.data?.contact) {
            resolvedContacts.push(response.data.contact)
          } else {
            this.warnUser({
              message: `A contact in this version (${contactRef.uuid}) could not be loaded and was removed.`,
            })
          }
        } catch {
          this.warnUser({
            message: `A contact in this version (${contactRef.uuid}) could not be found and was removed.`,
          })
        }
      }
      trip.contacts = resolvedContacts

      if (configuration.primaryContact?.uuid) {
        const primaryContact = resolvedContacts.find(contact => contact.uuid === configuration.primaryContact!.uuid)
        if (primaryContact) {
          trip.primaryContact = primaryContact
        } else {
          trip.primaryContact = null
          this.warnUser({
            message: 'Primary contact for this version could not be resolved.',
          })
        }
      } else {
        trip.primaryContact = null
      }
    },

    async storeItinerary(day: ItineraryInterface|null) {
      if (!this.trip?.uuid || !day) return null;

      const validatorResult = this.validateScheduledDay(day);
      if (!validatorResult.valid) {
        this.warnUser({
          message: validatorResult.message,
        })

        const tripBuilder = useTripBuilderStore();
        if (validatorResult.targetTab && (validatorResult.targetTab !== tripBuilder.selectedTab)) {
          tripBuilder.$patch({ selectedTab: validatorResult.targetTab })
        }

        return null;
      }

      if (!Array.isArray(day?.services)) {
        day.services = [];
      }

      day.services = day.services.filter((service: ItineraryService) => {
        return (service.service ?? null) !== null;
      });

      if ((!day.trip) && this.trip) {
        day.trip = { uuid: this.trip?.uuid }
      }

      const url = '/v1/itinerary/'+ (day.trip?.uuid ?? this.trip?.uuid);

      const tripDay: ItineraryInterface = {
        uuid: day.uuid,
        trip: { uuid: day.trip?.uuid ?? this.trip?.uuid },
        dateScheduled: day.dateScheduled,
        location: day.location,
        version: day.version,
      };
      day.services.forEach((service: ItineraryService) => {
        service.tripDay = tripDay;
      });

      return api.post(url, day)
        .then((response) => {
          const result = response.data.item;

          if (result?.uuid && !day.uuid) {
            // Backfill the day itself too, not just the reference copy its
            // services share. Anything that later reads `day.uuid` — adding an
            // activity, moving a service onto this day — would otherwise keep
            // treating a persisted day as unsaved.
            day.uuid = result.uuid;
            tripDay.uuid = result.uuid;
          }

          return result;
        })
        .catch((error) => this._handleMutationError(error))
        .finally(() => { this.setLoading(false) })
    },

    async storeActivity(item: ItineraryService|null) {
      if (!item?.service) {
        return item;
      }

      const payload: ItineraryService = { ...item };

      console.debug("storeActivity", payload)
      if (item.tripDay) {
        payload.trip = (
          payload.trip
          ? { ...payload.trip }
          : { uuid: item.tripDay.trip?.uuid ?? null }
        ) as Partial<TripInterface>;

        if (item.tripDay.uuid) {
          payload.tripDay = {
            uuid: item.tripDay.uuid,
          } as ItineraryInterface
        }
        else {
          // The day exists client side only, so there is no reference to send.
          // `{ uuid: undefined }` serializes to `{}`, which the API reads as an
          // empty itinerary item and builds a phantom trip day from.
          delete payload.tripDay
        }
      }

      payload.service = {
        uuid: item.service.uuid,
      }
      console.debug("storeActivity", payload, item)

      const url = '/v1/activity/'+ (payload.trip?.uuid ?? payload.tripDay?.trip?.uuid ?? this.trip?.uuid);
      if (!this.loading) {
        await this.setLoading(true)
      }

      return api.post(url, payload)
        .then((response) => {
          return response.data.service;
        })
        .catch((error) => this._handleMutationError(error))
        .finally(() => {
          this.setLoading(false)
        })
    },
    async removeActivity(item: ItineraryService | null) {
      console.debug("AppDataStore | removeActivity | item | ", item);
      if (!item) return;

      await this.setLoading(true)

      const activityDecision = async () => {
        if (item.uuid) {
          return api.delete('/v1/activity/' + item.uuid)
        }

        return { status: 200 }
      }

      return await activityDecision()
        .then((response) => {
          // console.debug("AppDataStore | removeActivity | ", response);

          if (![ 200,201,202 ].includes(response?.status ?? 200)) {
            return response;
          }

          this.trip?.itinerary?.forEach((day: ItineraryInterface) => {
            // console.debug("ActivitiesTab | ", day);
            // console.debug("ActivitiesTab | ", day.services.length);
            if (!day.services) return;

            day.services = day.services.filter((service: ItineraryService) => {
              return !isSameRecord(service, item);
            })
            // console.debug("AppDataStore | ", day.services.length);
          })

          return response;
        })
        .catch((error) => this._handleMutationError(error))
        .finally(() => {
          this.setLoading(false)
        })
    },
    async selectService(day: ItineraryInterface, item: ItineraryService, service: ServiceInterface) {
      return this.fetchService(service)
        .then((data: ServiceInterface) => {
          item.service = { ...data };

          if (data.pricingType === labels.services.pricing.type.group.value) {
            item.quantity = 1;
          }

          return data;
        })
        .then((data: ServiceInterface) => {
          return this.storeActivity(item)
            .then((activity: ItineraryService) => {
              if (!item.uuid) {
                item.uuid = activity.uuid;
              }

              return data;
            })
        })
        .then((data: ServiceInterface) => {
          if (!Array.isArray(day.services)) {
            day.services = []
          }

          if ((data.children ?? []).length > 0) {
            data.children?.forEach((bundledService: ServiceInterface) => {
              console.debug("Bundled Service:", bundledService);

              const additionalService: ItineraryService = {
                tripDay: item.tripDay,
                service: bundledService,
                markup: null,
                multiplier: 1,
                voucher: false,
                parent: item.uuid,
              }

              if (additionalService.service?.type?.label !== 'hotel') {
                if (additionalService.service?.pricingType === labels.services.pricing.type.group.value) {
                  additionalService.quantity = 1;
                }
              }

              day?.services?.push(additionalService);
              this.storeActivity(additionalService)
                .then((activity: ItineraryService) => {
                  additionalService.uuid = activity.uuid;
                  additionalService.service = activity.service;
                })
                .catch((error) => { handleError(error); })
            })
          }
        })
        .then(() => {
          return this.fetchTrip(day.trip as TripInterface)
        })
    },
    async selectPackage(day: ItineraryInterface, item: ItineraryService, template: BundleInterface) {
      return this.fetchPackage(template)
        .then((bundle: BundleInterface) => {
          if (!Array.isArray(day.services)) {
            day.services = []
          }

          console.debug("AppData | selectPackage", bundle);
          bundle.services?.forEach((bundledService: ServiceInterface, index: number) => {
            console.debug("Bundled Service:", bundledService);

            if (index === 0) {
              item.service = bundledService;
              item.markup = null;
            }
            else {
              const additionalService: ItineraryService = {
                tripDay: item.tripDay,
                service: bundledService,
                multiplier: 1,
                quantity: null,
                markup: null,
                bookingStatus: null,
              }

              if (bundledService?.pricingType === labels.services.pricing.type.group.value) {
                additionalService.quantity = 1;
              }

              day?.services?.push(additionalService);
              this.storeActivity(additionalService)
                .then((activity: ItineraryService) => {
                  console.debug("ActivitiesTab | onServiceChange | nested async action")
                  additionalService.uuid = activity.uuid;
                  additionalService.service = activity.service;
                })
                .catch((error) => { handleError(error); })
              ;
            }
          })

          return bundle;
        })
    },

    async updateTripAddFee(fee: FeeInterface) {
      if (!this.trip) return;

      if (this.trip && !Array.isArray(this.trip.fees)) {
        this.trip.fees = [];
      }

      this.trip.fees?.push(fee as FeeInterface)
    },
    async updateTripRemoveFee(item: FeeInterface) {
      if (!this.trip) return;

      this.trip.fees = this.trip?.fees?.filter((fee) => {
        return item !== fee;
      })
    },
    async updateItineraryService(service: ItineraryService) {
      console.info("AppDataStore | updateItineraryService");
      if (!this.trip?.itinerary) return;

      const tripDay = this.trip.itinerary.find((item: ItineraryInterface) => {
        return item.uuid === service.tripDay?.uuid;
      });

      if (tripDay) {
        let updatedItinerary = this.trip.itinerary.filter((item: ItineraryInterface) => {
          return item.uuid !== tripDay.uuid;
        })

        updatedItinerary.push(tripDay);
        updatedItinerary = updatedItinerary.sort(dateSorter)

        this.$patch({
          trip: { itinerary: updatedItinerary }
        })

        return service;
      }
    },
    async updateTripContext(item: TripInterface | null) {
      console.info("TripBuilder | updateTripContext")
      if (!item) {
        this.$patch({ trip: null })
      }
      else {
        await this.fetchTrip(item)
        if (this.trip) {
          await this.acquireLock(this.trip)
        }
      }
    },
    async createNewRevision(trip: TripInterface|null) {
      console.info("AppDataStore | createNewRevision");
      if (!trip?.currentVersion) return;

      this.setLoading(true);
      trip.currentVersion = null;

      // storeTrip's version-config sync refetches the trip once the backend has
      // allocated the new revision, so the new version's configuration and a fresh
      // trip are already in place — no separate fetchTrip needed here.
      return this.storeTrip(trip)
        .then((result) => {
          if (!result) return;

          this.trip = result;

          this.successMessageToUser({
            message: kendo.format(labels.notifications.revisionCreatedFormatString, result?.proposalName ?? result?.primaryContact?.name ?? 'Trip'),
          });

          return result;
        })
        .catch(() => {})
        .finally(() => { this.setLoading(false) });
    },
    async markDepositPaid(status: boolean) {
      if (!this.trip) return;

      this.setLoading(true);

      await this.$patch({
        trip: {
          currentVersion: {
            final: this.trip?.currentVersion?.final ? undefined : status
          },
          versions: this.trip?.versions?.map((item: VersionInterface) => {
            if (item.uuid === this.trip?.currentVersion?.uuid) {
              item.final = status;
            }

            return item;
          }),
          status: {
            depositPaid: status
          }
        }
      })

      await this.storeVersion(this.trip.currentVersion)
        .then(() => {
          return this.storeTrip(this.trip);
        })
        .then((trip: TripInterface | null | undefined) => {
          this.successMessageToUser({
            message: kendo.format(labels.notifications.savedFormatString, trip?.proposalName ?? trip?.primaryContact?.name ?? 'Trip'),
          });
        })
        .catch(() => {})
        .finally(() => {
          this.setLoading(false);
        })
    },
    async markFinalItinerary(status: boolean, toggledVersion: VersionInterface|null = null) {
      if (!this.trip) return Promise.resolve();
      if (!toggledVersion) {
        toggledVersion = this.trip?.currentVersion as VersionInterface;
      }

      let labelUpdate = status ? 'Final' : kendo.format('Revision {0}', toggledVersion?.revision);
      if (!status && toggledVersion?.revision === 0) {
        labelUpdate = 'Baseline';
      }

      await this.$patch({
        trip: {
          currentVersion: (toggledVersion === this.trip.currentVersion) ? {
            label: labelUpdate,
            final: status
          } : this.trip.currentVersion,
          versions: this.trip?.versions?.map((item: VersionInterface) => {
            if (item.uuid === toggledVersion?.uuid) {
              item.label = labelUpdate;
              item.final = status;
            }

            return item;
          })
        }
      })

      await this.storeVersion(toggledVersion)
        .then(() => {
          this.successMessageToUser({
            message: kendo.format(labels.notifications.savedFormatString, this.trip?.proposalName ?? this.trip?.primaryContact?.name ?? 'Trip'),
          });
        })
        .catch((error) => { handleError(error) })

      return toggledVersion as VersionInterface;
    },

    validateScheduledDay(day: ItineraryInterface): ValidatorResultInterface {
      if (!day.location) {
        return {
          valid: false,
          message: kendo.format("Location not set for {0:d}", this.agnosticDate(day.dateScheduled)),
        };
      }

      if (Array.isArray(day.services)) {
        const missingServices = day.services
          .filter((item: ItineraryService) => {
            console.debug(item);
            return (item.service ?? null) === null;
          });

        if (missingServices.length > 0) {
          return {
            valid: false,
            message: kendo.format("Missing services on {0:d}", this.agnosticDate(day.dateScheduled)),
          };
        }
      }

      return {
        valid: true,
      }
    },
    validateService(service: ServiceInterface): ValidatorResultInterface {
      if (!service.supplier) {
        return {
          valid: false,
          message: "Supplier is required.",
        };
      }

      if (!service.type) {
        return {
          valid: false,
          message: "Select a service type.",
        };
      }

      if (!service.country) {
        return {
          valid: false,
          message: "Select a country.",
        };
      }

      if ((service.name ?? "").length === 0) {
        return {
          valid: false,
          message: "Service name is required.",
        };
      }

      return {
        valid: true,
      }
    },
    validateTrip(trip: TripInterface): ValidatorResultInterface {
      console.debug(Array.isArray(trip.itinerary));
      if (Array.isArray(trip.itinerary)) {
        const countMissingLabels = trip.itinerary.filter((item) => {
          console.debug(item.location?.label ?? null);
          return (item.location?.label ?? null) === null;
        }).length;

        console.debug(countMissingLabels);
        if (countMissingLabels > 0) {
          return {
            valid: false,
            message: "Select cities for each day. Trip cannot be saved.",
            targetTab: 'cities',
          };
        }
      }

      let feeValidationResult = { valid: true };
      trip.fees?.forEach((fee: FeeInterface) => {
        const result = this.validateFee(fee);
        if (result.valid) return;

        feeValidationResult = { ...result };
      })

      return feeValidationResult;
    },
    validateFee(fee: FeeInterface): ValidatorResultInterface {
      if (!fee.label) {
        return {
          valid: false,
          message: "Fee description is required"
        };
      }

      if (!fee.type) {
        return {
          valid: false,
          message: "Select the fee type"
        };
      }

      if (!fee.rate && !fee.amount) {
        return {
          valid: false,
          message: "A rate or fixed fee amount is required"
        };
      }

      return {
        valid: true,
      }
    },

    async printStatus(){
      if (!this.printStatusUrl) {
        await this.setPrinting(false);
        return;
      }

      return await api.post(this.printStatusUrl)
        .then((response) => {
          return response.data;
        })
        .catch((error) => { handleError(error) })
    },
    async downloadFile(url: string) {
      return api.get(url,{
        headers: {
          'Content-Type': 'application/json',
        },
        'responseType': 'blob',
      })
        .then(response=> {
          // console.debug(response)
          if (response.status === 200) {
            const url = window.URL.createObjectURL(new Blob([response.data]));

            const filename = response.headers['content-disposition']?.split('=')[1]?.replaceAll('"', '');
            // console.debug(response.headers['content-disposition'], filename);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename ?? 'character-sheet.pdf');
            link.setAttribute('class', 'hidden');
            link.setAttribute('target', '_blank');

            document.body.appendChild(link);
            link.click();
            link.remove();
          }
        })
        .catch(error => { handleError(error) })
    },

    async printProposal(trip: TripInterface) {
      console.info("AppDataStore | printProposal")
      if (!trip) return;

      return api.post('/v1/print/'+ trip?.uuid +'/proposal')
        .then((response) => {
          console.debug("AppDataStore | printProposal | ", response);
          this.printStatusUrl = response?.data?.statusUrl ?? null;

          return response?.data;
        })
        .catch((error) => { handleError(error); });
    },
    async printVouchers(trip: TripInterface) {
      console.info("AppDataStore | printVouchers")
      if (!trip) return;

      return api.post('/v1/print/'+ trip?.uuid +'/vouchers')
        .then((response) => {
          console.debug("AppDataStore | printVouchers | ", response);
          this.printStatusUrl = response?.data?.statusUrl ?? null;

          return response?.data;
        })
        .catch((error) => { handleError(error); });
    },
    async printVoucher(item: ItineraryService) {
      console.info("AppDataStore | printVoucher")
      if (!item) return;

      return api.post('/v1/print/voucher/'+ item.uuid)
        .then((response) => {
          console.debug("AppDataStore | printVoucher | ", response);
          this.printStatusUrl = response?.data?.statusUrl ?? null;

          return response?.data;
        })
        .catch((error) => { handleError(error); });
    },
    async printInvoice(trip: TripInterface) {
      console.info("AppDataStore | printInvoice")
      if (!trip || !trip.agent || !trip.invoiceNumber || !trip.dateAgentInvoice) return;

      return api.post('/v1/print/'+ trip?.uuid +'/invoice')
        .then((response) => {
          console.debug("AppDataStore | printInvoice | ", response);
          this.printStatusUrl = response?.data?.statusUrl ?? null;

          return response?.data;
        })
        .catch((error) => { handleError(error); });
    },

    async fetchSuppliers(url: string | null, filters?: Array<FilterDescriptor>) {
      if (!url) {
        url = "/v1/supplier"
      }

      if (filters && (filters?.length ?? 0) > 0) {
        const compiled = this.filtersToQueryString(filters ?? []);
        url = url + (url.indexOf('?') >= 0 ? '&' : '?') + compiled;
      }

      await this.setLoading(true)
      await api.get(url)
        .then((response) => {
          this.$patch({
            suppliers: response.data.results,
            links: response.data.links,
            records: response.data.metadata,
          })
        }).then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async autocompleteSuppliers(filters?: Array<FilterDescriptor>){
      let url = "/v1/supplier"

      if (filters && (filters?.length ?? 0) > 0) {
        const compiled = this.filtersToQueryString(filters ?? []);
        url = url + (url.indexOf('?') >= 0 ? '&' : '?') + compiled;
      }

      await api.get(url)
        .then((response) => {
          useTripBuilderStore().$patch({
            autocomplete: {
              suppliers: response.data.results
            }
          })
        })
    },
    async fetchSupplier(supplier: SupplierInterface) {
      if (!supplier) return;
      this.setLoading(true)

      const url = '/v1/supplier/' + supplier.uuid;
      await api.get(url)
        .then((response) => {
          this.$patch({
            supplier: response.data,
          })
        }).then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleError(error);
          throw error;
        })
    },
    async storeSupplier(supplier: SupplierInterface | null) {
      console.info("AppDataStore | storeSupplier")
      if (!supplier) return;

      const payload = this._buildApiPayload(supplier);

      if (!supplier?.uuid) {
        this.setLoading(true)

        const url = '/v1/supplier';
        await api.post(url, payload)
          .then((response) => {
            this.$patch({
              supplier: response.data.supplier,
            })

          }).catch((error) => {
            console.error(error.message);
            console.debug(error);
          }).finally(() => {
            this.setLoading(false)
          })
      }
      else {
        this.setLoading(true)

        const url = '/v1/supplier';
        await api.patch(url, payload)
          .then((response) => {
            this.$patch({
              supplier: response.data.supplier,
            })

          }).catch((error) => {
            console.error(error.message);
            console.debug(error);
          }).finally(() => {
            this.setLoading(false)
          })
      }

      return Promise.resolve(this.supplier);
    },
    async removeSupplier(supplier: SupplierInterface | null) {
      if (!supplier || !supplier?.uuid) return;

      this.setLoading(true)

      await api.delete('/v1/supplier/' + supplier.uuid)
        .then((response) => {
          return Promise.resolve(response.data.supplier);

        })
        //.catch(error => handleError(error))
        .finally(() => {
          this.setLoading(false)
        })

      return Promise.resolve(supplier);
    },

    async fetchPackages(url: string | null, filters?: Array<FilterDescriptor>) {
      if (!url) {
        url = "/v1/package"
      }

      if (filters && (filters?.length ?? 0) > 0) {
        const compiled = this.filtersToQueryString(filters ?? []);
        url = url + '?' + compiled;
      }

      this.setLoading(true)
      return api.get(url)
        .then((response) => {
          this.$patch({
            bundles: response.data.results,
            links: {
              next: response.data.links.next,
              previous: response.data.links.previous
            },
            records: {
              total: response.data.metadata.total,
              start: response.data.metadata.start,
              end: response.data.metadata.end,
            },
          })
        })
        .then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async fetchPackage(bundle: BundleInterface) {
      await this.setLoading(true)

      const url = '/v1/package/' + bundle.uuid;
      return api.get(url)
        .then((response) => {
          this.$patch({
            bundle: response.data,
          })

          return response.data;
        })
        .then(async (bundle: BundleInterface) => {
          await this.setLoading(false)

          return bundle;
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async storePackage(item: BundleInterface | null) {
      if (!item?.uuid) {
        this.setLoading(true)

        const url = '/v1/package';
        await api.post(url, item)
          .then((response) => {
            this.$patch({
              bundle: response.data.package,
            })

            return Promise.resolve(response.data.package);
          }).catch((error) => {
            console.error(error.message);
            console.debug(error);
          }).finally(() => {
            this.setLoading(false)
          })
      }
      else {
        this.setLoading(true)

        const url = '/v1/package';
        await api.patch(url, item)
          .then((response) => {
            this.$patch({
              bundle: response.data.package,
            })

            return Promise.resolve(response.data.package);
          }).catch((error) => {
            console.error(error.message);
            console.debug(error);
          }).finally(() => {
            this.setLoading(false)
          })
      }
    },
    async removePackage(item: BundleInterface | null) {
      if (!item || !item?.uuid) return;

      this.setLoading(true)

      await api.delete('/v1/package/' + item.uuid)
        .then((response) => {
          return Promise.resolve(response.data.package);

        })
        //.catch(error => handleError(error))
        .finally(() => {
          this.setLoading(false)
        })
    },

    async fetchServices(url: string | null, filters?: Array<FilterDescriptor>) {
      if (!url) {
        url = "/v1/service"
      }

      if (filters && (filters?.length ?? 0) > 0) {
        const compiled = this.filtersToQueryString(filters ?? []);
        url = url + (url.indexOf('?') > 0 ? '&' : '?') + compiled;
      }

      return api.get(url)
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async fetchService(service: ServiceInterface, clone?: boolean) {
      this.setLoading(true)

      const url = '/v1/service/' + service.uuid + ((clone === true) ? '?clone=1' : '');
      return api.get(url)
        .then((response) => {
          console.debug("AppData | fetchService | processing response")
          this.$patch({
            service: response.data,
          })

          return response.data;
        })
        .then((data: ServiceInterface) => {
          console.debug("AppData | fetchService | cancel loading indicator")
          this.setLoading(false)

          return data;
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async cloneService(service: ServiceInterface) {
      console.info("AppDataStore | cloneService");
      //console.debug("AppDataStore | cloneService | ", service);
      if (!service?.uuid) return Promise.resolve(service);

      return this.fetchService(service, true)
    },
    async storeService(service: ServiceInterface | null) {
      console.info("AppDataStore | storeService")
      if (service === null) return null;

      const validatorResult = this.validateService(service);
      console.debug(validatorResult);
      if (!validatorResult?.valid) {
        this.warnUser({
          message: validatorResult.message,
        })

        return null;
      }

      const payload = this._buildApiPayload(service);
      let saved = false

      if (!service?.uuid) {
        await this.setLoading(true)

        const url = '/v1/service';
        await api.post(url, payload)
          .then((response) => {
            this.service = response.data.service
            saved = true
          })
          .catch(error => handleError(error))
          .finally(() => {
            this.setLoading(false)
          })
      }
      else {
        await this.setLoading(true)

        const url = '/v1/service';
        await api.patch(url, payload)
          .then((response) => {
            this.$patch({
              service: response.data.service,
            })
            saved = true
          })
          .catch(error => handleError(error))
          .finally(() => {
            this.setLoading(false)
          })
      }

      // Null when nothing was saved, as for a service that fails validation: the error has been shown already.
      return saved ? service : null
    },
    async removeService(item: ServiceInterface | null) {
      if (!item || !item?.uuid) return;

      this.setLoading(true)

      await api.delete('/v1/service/' + item.uuid)
        .then((response) => {
          return Promise.resolve(response.data.service);

        })
        //.catch(error => handleError(error))
        .finally(() => {
          this.setLoading(false)
        })
    },
    validService(service: ServiceInterface | null) {
      if (!service) return false;

      const validatorResult = this.validateService(service);

      return validatorResult?.valid ?? false;
    },

    async fetchContacts(url: string | null, filters?: Array<FilterDescriptor>) {
      if (!url) {
        url = "/v1/contact"
      }

      if (filters && (filters?.length ?? 0) > 0) {
        const compiled = this.filtersToQueryString(filters ?? []);
        url = url + '?' + compiled;
      }

      this.setLoading(true)
      await api.get(url)
        .then((response) => {
          this.$patch({
            contacts: response.data.results,
            links: {
              next: response.data.links.next,
              previous: response.data.links.previous
            },
            records: {
              total: response.data.metadata.total,
              start: response.data.metadata.start,
              end: response.data.metadata.end,
            },
          })
        })
        .then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async autocompleteContacts(namePartial: string, filters?: Array<FilterDescriptor>){
      let url = "/v1/contact"

      if (filters && (filters?.length ?? 0) > 0) {
        const compiled = this.filtersToQueryString(filters ?? []);
        url = url + '?' + compiled;
      }

      await api.get(url)
        .then((response) => {
          useTripBuilderStore().$patch({
            autocomplete: {
              contacts: response.data.results
            }
          })
        })
    },
    async fetchContact(contact: ContactInterface) {
      this.setLoading(true)

      const url = '/v1/contact/' + contact.uuid;
      await api.get(url)
        .then((response) => {
          this.$patch({
            contact: response.data,
          })
        })
        .then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async storeContact(contact: ContactInterface | null) {
      if (!contact) return null;

      await this.setLoading(true)

      if (!contact?.uuid) {
        const url = '/v1/contact';
        console.debug("AppData | storeContact", url, contact)
        await api.post(url, contact)
          .then((response) => {
            this.$patch({
              contact: response.data.contact,
            })

            contact = response.data.contact;
          }).catch((error) => {
            console.error(error.message);
            console.debug(error);
            handleError(error);
          }).finally(() => {
            this.setLoading(false)
          })
      }
      else {
        const url = '/v1/contact';
        await api.patch(url, contact)
          .then((response) => {
            this.$patch({
              contact: response.data.contact,
            })

            contact = response.data.contact;
          }).catch((error) => {
            console.error(error.message);
            console.debug(error);
          }).finally(() => {
            this.setLoading(false)
          })
      }

      return Promise.resolve(contact)
    },
    async storeComment(comment: CommentInterface | null) {
      console.info("AppDataStore | storeComment");

      if (!comment) return;

      if (!comment.uuid) {
        return await api.post('/v1/comment', comment)
          .then((response) => {
            return Promise.resolve(response)
          })

      } else {
        return await api.patch('/v1/comment', comment)
          .then((response) => {
            return Promise.resolve(response)
          })
      }
    },
    async removeContact(item: ContactInterface | null) {
      if (!item || !item?.uuid) return;

      this.setLoading(true)

      await api.delete('/v1/contact/' + item.uuid)
        .then((response) => {
          return Promise.resolve(response.data.contact);

        })
        //.catch(error => handleError(error))
        .finally(() => {
          this.setLoading(false)
        })
    },
    validContact(contact: ContactInterface | null) {
      if (!contact) return false;

      if ((contact.name?.length ?? 0) > 0) {
        if (contact.type) {
          return true;
        }
      }

      return false;
    },

    async fetchMedia(url: string | null) {
      if (!url) {
        url = "/v1/media"
      }

      this.setLoading(true)
      await api.get(url)
        .then((response) => {
          this.$patch({
            media: response.data.results,
            links: {
              next: response.data.links.next,
              previous: response.data.links.previous
            },
            records: {
              total: response.data.metadata.total,
              start: response.data.metadata.start,
              end: response.data.metadata.end,
            },
          })
        })
        .then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async deleteMedia(asset: MediaInterface) {
      await this.setLoading(true);

      return await api.delete("/v1/media/"+ asset.uuid)
        .then(() => {
          this.setLoading(false);

        }).catch(error => handleError(error))
    },

    async fetchCountries() {
      this.setLoading(true)
      await api.get("/v1/country")
        .then((response) => {
          this.$patch({ countries: response.data })
        })
        .then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async fetchCountry(country: CountryInterface | null) {
      if (!country) return;

      this.setLoading(true)
      await api.get('/v1/country/' + country?.uuid)
        .then((response) => {
          this.$patch({
            country: response.data,
          })
        }).then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async storeCountry(country: CountryInterface | null) {
      console.info("AppDataStore | storeCountry");

      if (!country) return;

      if (!country.uuid) {
        return await api.post('/v1/country', country)
          .then((response) => {
            return Promise.resolve(response)
          })

      } else {
        return await api.patch('/v1/country', country)
          .then((response) => {
            return Promise.resolve(response)
          })
      }
    },

    async fetchRegions() {
      if (!this.loading) this.setLoading(true);

      await api.get("/v1/region")
        .then((response) => {
          this.$patch({ regions: response.data })
        }).then(() => {
          this.setLoading(false)
        })
    },
    async fetchRegion(region: RegionInterface | null) {
      if (!region) return;

      this.setLoading(true)
      await api.get('/v1/region/' + region?.uuid)
        .then((response) => {
          this.$patch({
            region: response.data,
          })
        }).then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async storeRegion(region: RegionInterface | null) {
      console.info("AppDataStore | storeRegion");

      if (!region) return;

      if (!region.uuid) {
        return await api.post('/v1/region', region)
          .then((response) => {
            return Promise.resolve(response)
          })

      } else {
        return await api.patch('/v1/region', region)
          .then((response) => {
            return Promise.resolve(response)
          })
      }
    },

    async fetchAppSettings() {
      this.setLoading(true)
      await api.get("/v1/settings")
        .then((response) => {
          this.$patch({ settings: response.data })
        }).then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async fetchPermissions() {
      this.setLoading(true)
      await api.get("/v1/settings/permissions")
        .then((response) => {
          this.$patch({ permissions: response.data })
        })
        .then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async storeSettings(settings: AppSettingsInterface) {
      this.setLoading(true)
      const url = '/v1/settings';
      await api.post(url, settings)
        .then((response) => {
          this.$patch({
            settings: response.data.settings,
          })
        }).catch((error) => {
          console.error(error.message);
          console.debug(error);
        }).finally(() => {
          this.setLoading(false)
        })

    },

    async fetchTypes() {
      this.setLoading(true)
      await api.get("/v1/tags/service")
        .then((response) => {
          this.$patch({ types: response.data })
        }).then(() => {
          this.setLoading(false)
        })
    },
    async autocompleteTags(namePartial: string | null, type?: string){
      const searchParams = new URLSearchParams();
      searchParams.append('limit', '50');
      if (namePartial !== null) {
        searchParams.append('q', namePartial);
      }

      let url = "/v1/tags/search"
      if (type) {
        url = "/v1/tags/"+ type
      }
      await api.get(url +"?" + searchParams.toString())
        .then((response) => {
          useTripBuilderStore().$patch({
            autocomplete: {
              tags: response.data
            }
          })
        })
    },
    async storeTag(tag: TagInterface) {
      const url = '/v1/tags';
      let item = null;
      await api.post(url, tag)
        .then((response) => {
          this.$patch({
            settings: response.data.tag,
          })

          item = response.data.tag;

        })
        // .catch(error => handleError(error))
        .finally(() => {
          this.setLoading(false)
        })

      return Promise.resolve(item)
    },

    async fetchUsers(url: string | null) {
      if (!url) url = "/v1/user/search";

      this.setLoading(true)
      await this.fetchRoles();
      await api.get(url)
        .then((response) => {
          this.$patch({ users: response.data.results, links: response.data.links })
        })
        .then(() => {
          this.setLoading(false)
        })
        .catch((error) => {
          handleErrorResponse(error);
          throw error;
        })
    },
    async autocompleteUsers(namePartial: string, filters: Record<string, string | string[]>){
      const searchParams = new URLSearchParams();
      searchParams.append('limit', '50');
      searchParams.append('q', namePartial);
      searchParams.append('filters', JSON.stringify(filters));

      await api.get("/v1/user/search?" + searchParams.toString())
        .then((response) => {
          useTripBuilderStore().$patch({
            autocomplete: {
              users: response.data.results
            }
          })
        })
    },
    async storeUser(user: AdminUserInterface) {

      user.roles =  user?.roles?.filter(item => {
        return item.uuid !== null;
      }).map(item => {
        return { uuid: item.uuid };
      })

      if (!user.uuid) {
        await api.post("/v1/user", user)
          .then((response) => {
            return Promise.resolve(response)
          })
      }
      else {
        await api.patch("/v1/user", user)
          .then((response) => {
            return Promise.resolve(response)
          })
      }
    },
    async fetchSalesTeam() {
      this.setLoading(true)
      await api.get("/v1/user/sales-team")
        .then((response) => {
          this.salesTeam = response.data.results;
        }).then(() => {
          this.setLoading(false)
        })
    },
    async fetchOpsTeam() {
      this.setLoading(true)
      await api.get("/v1/user/ops-team")
        .then((response) => {
          this.opsTeam = response.data.results;
        }).then(() => {
          this.setLoading(false)
        })
    },
    async fetchRoles(cancelLoadIndicator: boolean = false) {
      if (!this.loading) this.setLoading(true)

      await api.get("/v1/role/search")
        .then((response) => {
          this.$patch({ roles: response.data })
        }).then(() => {
          if (cancelLoadIndicator) {
            this.setLoading(false)
          }
        })
    },
    async updateUserRoles(user: AdminUserInterface) {
      const roles =  user?.roles?.filter(item => {
        return item.uuid !== null;
      }).map(item => {
        return { uuid: item.uuid };
      })

      const data = {
        uuid: user.uuid,
        roles: roles
      }

      await api.patch("/v1/user", data);
    },

    setLoading(status: boolean) {
      _loadingCount += status ? 1 : -1
      _loadingCount = Math.max(0, _loadingCount)
      this.loading = _loadingCount > 0
    },
    async setPrinting(status: string|boolean) {
      await this.$patch({
        printStatusUrl: !status ? null : this.printStatusUrl
      })
    },

    agnosticDate(dateString: string|number|null|undefined): Date | undefined {
      if (dateString && /([+-]\d{2}:?\d{2}|Z)$/.test(dateString.toString())) {
        return new Date(dateString);
      }
      else if (dateString) {
        console.debug("AppData | agnosticDate | ", dateString + this.clientTimezoneInterval);
        return new Date(dateString + this.clientTimezoneInterval);
      }

      return undefined;
    },
    generateNewRevision(trip: TripInterface | null): VersionInterface {
      console.info("AppDataStore | generateNewRevision");

      if ((!trip?.currentVersion) && ((trip?.versions?.length ?? 0) === 0)) {

        // Create the Baseline
        return {
          label: labels.planner.revisions.baseline,
          revision: 0,
        } as VersionInterface;
      }

      // Create a new revision
      const version = {
        trip: { uuid: trip?.uuid },
        revision: trip?.versions?.length ?? 1,
        label: "",
      } as VersionInterface;

      version.label = kendo.format(labels.planner.revisions.labelFormat, version.revision)

      return version;
    },

    clearTooltips() {
      const visibleTooltips = document.querySelectorAll("[role=tooltip]");
      [...visibleTooltips].map(element => element.remove())
    },
    clearContext() {
      this.$patch({
        trip: undefined,
        service: undefined,
        supplier: undefined,
        bundle: undefined,
        user: undefined,
        contact: undefined,
        asset: undefined
      })
    },
    resetFilters() {
      this.$patch({
        filters: {
          searchQuery: "",
        }
      })
    },

    reverseItinerary() {
      console.info("AppData | reverseItinerary");
      if (!this.trip?.itinerary) return Promise.resolve();
      if (this.cities.length < 2) return Promise.resolve();

      const dateDeparture = this.agnosticDate(this.trip.dateDeparture) ?? new Date();
      dateDeparture.setHours(12, 0, 0, 0);

      // Build UUID -> new date map from reversed city order
      const reversedCities = [...this.cities].reverse();
      const dateMap = new Map<string, string>();
      reversedCities.forEach((city: ItineraryInterface, index: number) => {
        if (city.uuid) {
          dateMap.set(city.uuid, kendo.formatDate(dateDeparture.addDays(index), Date.prototype.TZ_AGNOSTIC_FORMAT));
        }
      });

      // Apply new dates directly to trip.itinerary items
      const updatedItinerary = this.trip.itinerary.map((item: ItineraryInterface) => {
        const newDate = dateMap.get(item.uuid ?? '');
        if (!newDate) {
          return item;
        }

        return {
          ...item,
          dateScheduled: newDate,
        }
      })

      if (updatedItinerary.length === this.trip.itinerary.length) {
        console.debug("AppData | reverseItinerary", updatedItinerary);

        this.trip.itinerary = updatedItinerary;

        return this.storeTrip(this.trip)
          .then((trip: TripInterface|null) => {
            if (trip !== null) {
              this.successMessageToUser({
                message: kendo.format(labels.notifications.savedFormatString, this.trip?.proposalName ?? this.trip?.primaryContact?.name ?? 'Trip')
              })
            }
          })
          .catch(() => {})
      }

      return Promise.resolve();
    },
    handleDepartureDateChange(dateDeparture: Date) {
      console.info("AppData | handleDepartureDateChange");
      console.debug("AppData | handleDepartureDateChange", dateDeparture);

      // Update the departure date
      dateDeparture.setHours(12,0,0,0);

      console.debug("AppData | handleDepartureDateChange | ", dateDeparture, kendo.formatDate(dateDeparture, Date.prototype.TZ_AGNOSTIC_FORMAT))

      this.$patch({
        trip: {
          dateDeparture: kendo.formatDate(dateDeparture, Date.prototype.TZ_AGNOSTIC_FORMAT),
          duration: this.cities.length
        }
      })

      // Shuffle the itinerary days
      this.cities?.forEach((element: ItineraryInterface, index: number) => {
        element.dateScheduled = kendo.formatDate(dateDeparture?.addDays(index), Date.prototype.TZ_AGNOSTIC_FORMAT);
        console.debug("AppData | handleDepartureDateChange | ", element.dateScheduled)
      });

      // Store the trip data
      return this.storeTrip(this.trip)
        .then((trip: TripInterface|null) => {
          if (trip !== null) {
            this.successMessageToUser({
              message: kendo.format(labels.notifications.savedFormatString, this.trip?.proposalName ?? this.trip?.primaryContact?.name ?? 'Trip')
            })
          }
        })
        .catch(() => {})
    },
    prependTravelDate() {
      console.info("AppData | prependTravelDate");
      if (!Array.isArray(this.trip?.itinerary)) {
        this.$patch({
          trip: {
            itinerary: []
          }
        })
      }

      const startDate = (this.agnosticDate(this.cities[0]?.dateScheduled ?? "") ?? new Date()).addDays(-1);
      console.debug("AppData | prependTravelDate", this.cities[0], startDate);
      const travelDayToAdd = useTripBuilderStore().newTravelDay(this.trip as TripInterface, startDate);
      console.debug("AppData | prependTravelDate", travelDayToAdd);
      if (!travelDayToAdd) return Promise.resolve();

      this.trip?.itinerary?.unshift(travelDayToAdd)
      return this.handleDepartureDateChange(this.agnosticDate(travelDayToAdd?.dateScheduled ?? "") ?? new Date());
    },
    appendTravelDate() {
      if (!this.trip) return Promise.resolve();

      const mostRecentDate = this.cities[this.cities.length - 1]?.dateScheduled;
      const endDate = (this.agnosticDate(mostRecentDate ?? "") ?? new Date()).addDays(1);

      const travelDay = useTripBuilderStore().newTravelDay(this.trip as TripInterface, endDate);
      if (!travelDay) return Promise.resolve();

      this.trip?.itinerary?.push(travelDay)
      return this.handleDepartureDateChange(this.agnosticDate(this.trip?.dateDeparture ?? "") ?? new Date())
    },

    lineItemTotal(lineItem: ItineraryInterface): number {
      console.info("AppDataStore | lineItemTotal")
      let total = 0;

      lineItem.services?.forEach((service: ItineraryService) => {
        if (!service?.service) return;

        const subtotal = this.serviceSubtotal(service);

        // console.debug(service, total);
        // console.debug(service.price_usd);
        total = total + roundNumbers(subtotal ?? 0);
      })

      return total;
    },
    serviceSubtotal(service: ItineraryService) {
      console.info("AppDataStore | serviceSubtotal")
      //console.debug("serviceSubtotal", service.service);
      if (!service?.service) return 0;
      if ((service?.subtotal ?? -1) >= 0) {
        return service.subtotal;
      }

      if (service.service?.type?.label === 'hotel') {
        return this.hotelSubtotal(service);
      }
      else {
        return this.activitySubtotal(service);
      }
    },
    hotelSubtotal(service: ItineraryService) {
      console.info("AppDataStore | hotelSubtotal")
      if (!service?.service) return 0;

      const dailyPrices = useTripBuilderStore().dailyPriceInDollars(service.service, service.quantity ?? 1, service.classification ?? null);
      const day = this.trip?.itinerary?.filter((element: ItineraryInterface) => {
        return element.uuid === (service.tripDay?.uuid ?? null);
      }).at(-1);

      //console.debug("hotelSubtotal", day);
      let pricingDate = new Date(day?.dateScheduled ?? "");
      if (service?.time) {
        const serviceTime = new Date(service?.time ?? "");

        //console.debug("hotelSubtotal", serviceTime);
        pricingDate.setHours(serviceTime?.getHours() ?? 15, serviceTime?.getMinutes() ?? 0, serviceTime?.getSeconds() ?? 0, 0);
      }
      const checkoutDate = (new Date(day?.dateScheduled ?? ""))
        .addDays(service.quantity ?? 1);
      checkoutDate.setHours(0, 0, 0, 0);

      let subtotal = 0;
      const multiplier = service.multiplier ?? 1;
      const markupPercent = service?.markup ?? service.service?.type?.markup ?? 0;

      //console.debug("hotelSubtotal", pricingDate, checkoutDate);
      while (pricingDate.getTime() < checkoutDate.getTime()) {
        //console.debug("hotelSubtotal", pricingDate.toISOString(), checkoutDate.toISOString());

        const dayIndex = pricingDate.getDay();
        let currentPrice = 0;

        const threshold = dailyPrices[dayIndex] ?? null;
        if (!useTripBuilderStore().hasNetPrice(service) && threshold) {
          if (service.price_usd) {
            currentPrice = service.price_usd
          }
          else if (threshold.price_usd) {
            currentPrice = threshold.price_usd;
          }

          if (threshold.price_euro) {
            const convertedPrice = threshold.price_euro * (threshold.exchangeRate ?? this.settings.exchangeRate ?? 1);
            currentPrice = Math.round((convertedPrice + Number.EPSILON) * 100) / 100;
          }
        }
        else {
          const convertedPrice = (service.price_euro ?? 0) * (service.exchangeRate ?? this.settings.exchangeRate ?? 1);
          currentPrice = Math.round((convertedPrice + Number.EPSILON) * 100) / 100;
        }

        let markupTotal = 0;
        if (markupPercent > 0) {
          markupTotal = roundNumbers((currentPrice ?? 0) * markupPercent);
        }

        subtotal = subtotal + (((currentPrice ?? 0) + markupTotal) * multiplier);

        pricingDate = pricingDate.addDays(1);
      }

      return roundNumbers(subtotal);
    },
    activitySubtotal(service: ItineraryService) {
      console.info("AppDataStore | activitySubtotal")

      const multiplier = service.multiplier ?? 1;
      const currentPrice = useTripBuilderStore().servicePriceInDollars(service, this.settings);
      let quantity = service?.quantity ?? this.trip?.numberOfPassengers ?? 1;
      if (service?.service?.pricingType === 'per-group') {
        quantity = 1;
      }

      let markupTotal = 0;
      const markupPercent = service?.markup ?? service.service?.type?.markup ?? 0;
      if (markupPercent >= 0) {
        markupTotal = roundNumbers((currentPrice ?? 0) * markupPercent);
      }

      //console.debug("AppDataStore | activitySubtotal", service.name ?? service.service?.name ?? null, currentPrice, markupPercent, markupTotal, quantity, multiplier);
      const subtotal = (((currentPrice ?? 0) + markupTotal) * quantity) * multiplier;

      return roundNumbers(subtotal);
    },

    commissionAmount(proposalTotal: number) {
      const commissionPercent = this.commissionPercent(this.trip)
      if (!commissionPercent || (commissionPercent <= 0)) {
        return 0;
      }

      const commissionMultiple = (commissionPercent / (1 - commissionPercent));
      console.debug("CommissionAmount | multiple", commissionPercent, commissionMultiple);
      const commission = proposalTotal * commissionMultiple;
      return roundNumbers(commission);
    },
    commissionPercent(trip?: TripInterface|null) {
      if (trip) {
        if ((trip.commissionOverride !== null) && (trip.commissionOverride !== undefined)) {
          return trip.commissionOverride;
        }

        if (trip.agent && (trip.agent.commissionOverride !== null) && (trip.agent.commissionOverride !== undefined)) {
          return trip.agent.commissionOverride;
        }
      }

      return this.settings.agentCommission ?? 0;
    },

    hasExceptionFor(user: UserInterface) {
      const filtered = this.trip?.accessExceptions?.filter((item) => {
        return item.uuid === user.uuid;
      });

      return (filtered?.length ?? 0) > 0;
    },
    toggleRole(user: AdminUserInterface, role: RoleInterface) {
      console.info("AppDataStore | toggleRole");

      if (!user) return;
      if (!user.roles) {
        user.roles = [];
      }

      if (this.userInRole(user, role.uuid ?? "")) {
        user.roles = user.roles.filter(item => {
          return item.uuid !== role.uuid;
        });
      }
      else {
        user.roles.push(role);
      }

      this.updateUserRoles(user);
    },
    userInRole(user: AdminUserInterface, uuid: string): boolean {
      const filtered = user?.roles?.filter((item) => {
        return item.uuid === uuid;
      });

      return (filtered ?? []).length > 0;
    },

    successMessageToUser(notice: { valid?: boolean, message?: string }) {
      if (notice?.message && !this.notifications.success.includes(notice.message)) {
        this.$patch({
          notifications: {
            success: [
              notice.message,
              ...this.notifications.success,
            ]
          }
        })
      }
    },
    warnUser(warning: { valid?: boolean, message?: string }) {
      console.warn("⚠️ WARNING: ", warning.message);
      if (warning?.message && !this.notifications.warnings.includes(warning.message)) {
        this.$patch({
          notifications: {
            warnings: [
              warning.message,
              ...this.notifications.warnings,
            ]
          }
        })
      }
    },
    _buildApiPayload(data: SupplierInterface | ServiceInterface) {
      type ApiPayload = {
        services?: ServiceInterface[]
        itinerary?: ItineraryInterface[]
        currentPrice?: ServicePriceInterface
        versions?: VersionInterface[]
        pricing?: unknown
        supplier?: SupplierInterface | { uuid?: string }
        region?: { uuid?: string }
        country?: { uuid?: string }
        media?: MediaInterface[]
        children?: ServiceInterface[]
      }
      const info: ApiPayload = toRaw(data) as ApiPayload;

      if (Array.isArray(info?.services)) {
        (info.services as ItineraryService[])?.forEach((item: ItineraryService) => {
          if (!item.tripDay?.uuid) return;

          item.tripDay = { uuid: item.tripDay.uuid } as ItineraryInterface;
          if (item.service?.uuid) {
            item.service = { uuid: item.service.uuid } as ServiceInterface
          }
          if (item.trip?.uuid) {
            item.trip = { uuid: item.trip.uuid }
          }
        });
      }

      const itinerary = info?.itinerary;
      if (itinerary) {
        itinerary.forEach((_item: ItineraryInterface, idx: number) => {
          const day = itinerary[idx];
          if (!day || day.services?.length === 0) return;

          day.services = itinerary[0]?.services?.map((item: ItineraryService) => {
            return { uuid: item.uuid }
          });
        });
      }

      if (info?.currentPrice) {
        delete info.currentPrice;
      }

      if (Array.isArray(info?.versions)) {
        info.versions = info.versions.map((item: VersionInterface) => {
          return { uuid: item.uuid };
        })
      }

      // Prices have their own endpoints, so a service never carries them.
      delete info.pricing;

      if (info?.supplier && info.supplier?.uuid) {
        info.supplier = { uuid: info.supplier.uuid }
      }

      if (info?.region && info.region?.uuid) {
        info.region = { uuid: info.region.uuid }
      }

      if (info?.country && info.country?.uuid) {
        info.country = { uuid: info.country.uuid }
      }

      if (Array.isArray(info?.media)) {
        info.media = info.media.map((el: MediaInterface) => { return { uuid: el.uuid } });
      }

      if (Array.isArray(info?.children)) {
        info.children = info.children.map((el: ServiceInterface) => { return { uuid: el.uuid } });
      }

      return info;
    }
  }
})
